import React from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Select } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux'
import { type RootState } from '../../../store/store'
import { updateUnitReview } from '../../../store/actionsCreators/unitsActions'
import { getRelatedUnitId as getRelatedRelistUnitId, getUnitReviewActivityEvents, type OffMarketActivityEvent, type RelistActivityEvent, type UnitActivityEvent } from '../../../functions/shared/units/unitReviewActivity'
import { buildBsoActivitySummary, buildBsoUnitAnalyticsRows, buildBsoUnitStatistics, formatAdminDubaiDate, formatUnitReviewReasonLabels, getRelatedUnitId, getUnitReviewReasonLabel, getUnitReviewSelectOptions, isManualBsoAction, type BsoStatisticsUnitDetailItem, type BsoUnitAnalyticsRow, type BsoUnitReview, type EntityBreakdownSummary, type UnitReviewCategory, type UnitReviewReason, type StatisticPeriod } from './adminBsoUnitStatistics'
import { getAdvertisementLinks, getAgentDisplay } from './adminBsoUnitActivity'
import { ManualAdvertisementModal } from './manualOperations/ManualAdvertisementModal'
import { ManualOfferModal } from './manualOperations/ManualOfferModal'
import { ManualViewingModal } from './manualOperations/ManualViewingModal'
import './AdminBsoUnitStatisticsPage.scss'

const EmptyValue = (): JSX.Element => <span className="text-muted">—</span>

const ACTIVITY_NAV_STATE = { fromBsoStatistics: true }
const ANALYTICS_PAGE_SIZE = 35
const ANALYTICS_PAGE_QUERY_PARAM = 'analyticsPage'

type UnitReviewModalState = {
    row: BsoUnitAnalyticsRow
    temporaryReasons: UnitReviewReason[]
    permanentReasons: UnitReviewReason[]
    saving: boolean
    error: string | null
} | null

type DetailsOverlayState = {
    title: string
    subtitle: string
    items: BsoStatisticsUnitDetailItem[]
} | null

type MarketStatusFilter = 'all' | 'active' | 'off-market'
type ListingTypeFilter = 'all' | 'rent' | 'sale'
type AnalyticsSort = 'newest' | 'oldest' | 'price-desc' | 'price-asc'

type OccupiedUntilAlert = {
    label: 'Occupied ends today' | 'Occupied expired'
    tone: 'today' | 'expired'
}

const MARKET_STATUS_FILTERS: Array<{ value: MarketStatusFilter, label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'off-market', label: 'Off Market' }
]

const LISTING_TYPE_FILTERS: Array<{ value: ListingTypeFilter, label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'rent', label: 'Rent' },
    { value: 'sale', label: 'Sale' }
]

const StatusBadgeWithMeta = ({ status, occupiedUntil }: { status: string, occupiedUntil?: string | null }): JSX.Element => (
    <span className="bso-statistics__status-badge-wrap">
        <span className="badge badge-light">{status}</span>
        {status === 'Occupied' && occupiedUntil != null && (
            <small className="bso-statistics__status-until">Until {formatAdminDubaiDate(occupiedUntil)}</small>
        )}
    </span>
)

const parsePositiveInt = (value: string | null): number => {
    const numeric = Number(value)
    return Number.isInteger(numeric) && numeric > 0 ? numeric : 1
}

const UnitReviewCell = ({ unitReview, activity = [] }: { unitReview: BsoUnitReview | null, activity?: UnitActivityEvent[] }): JSX.Element => {
    if (unitReview == null && activity.length === 0) return <EmptyValue />
    const relistedFrom = activity.filter((event): event is RelistActivityEvent => event.type === 'relisted' && event.direction === 'from')
    const relistedTo = activity.filter((event): event is RelistActivityEvent => event.type === 'relisted' && event.direction === 'to')
    const offMarket = activity.filter((event): event is OffMarketActivityEvent => event.type === 'off_market')

    return (
        <div className="bso-statistics__unit-review">
            {unitReview != null && unitReview.temporaryReasons.length > 0 && <div className="bso-statistics__unit-review-section"><span>TEMPORARY PROBLEMS</span><small>{formatUnitReviewReasonLabels(unitReview.temporaryReasons)}</small></div>}
            {unitReview != null && unitReview.permanentReasons.length > 0 && <div className="bso-statistics__unit-review-section"><span>PERMANENT PROBLEMS</span><small>{formatUnitReviewReasonLabels(unitReview.permanentReasons)}</small></div>}
            {offMarket.length > 0 && <div className="bso-statistics__unit-review-section"><span>OFF MARKET</span>{offMarket.map((event, index) => <small key={`${event.createdAt}-${String(index)}`}>{event.reason === 'other_offer_completed' ? 'Other offer completed' : event.reason === 'landlord_removed_unit' ? 'Landlord removed the unit' : 'Other'} · {formatAdminDubaiDate(event.createdAt)}</small>)}</div>}
            {relistedFrom.length > 0 && <div className="bso-statistics__unit-review-section"><span>RELISTED FROM</span>{relistedFrom.map((event, index) => <Link key={`${event.createdAt}-${String(index)}`} to={`/landlord/modalView/unit_modal/${String(getRelatedRelistUnitId(event))}/not/catalog`}>Unit #{String(getRelatedRelistUnitId(event))}</Link>)}</div>}
            {relistedTo.length > 0 && <div className="bso-statistics__unit-review-section"><span>RELISTED TO</span>{relistedTo.map((event, index) => <Link key={`${event.createdAt}-${String(index)}`} to={`/landlord/modalView/unit_modal/${String(getRelatedRelistUnitId(event))}/not/catalog`}>Unit #{String(getRelatedRelistUnitId(event))}</Link>)}</div>}
            {false && activity.length > 0 && <div className="bso-statistics__unit-history">
                <small>History</small>
                {activity.map((event, index) => event.type === 'relisted'
                    ? <Link key={`${event.createdAt}-${String(index)}`} to={`/landlord/modalView/unit_modal/${String(getRelatedRelistUnitId(event))}/not/catalog`}>{event.direction === 'to' ? 'Relisted to' : 'Relisted from'} Unit #{String(getRelatedRelistUnitId(event))}</Link>
                    : <small key={`${event.createdAt}-${String(index)}`}>Off market — {event.reason === 'other_offer_completed' ? 'other offer completed' : event.reason === 'landlord_removed_unit' ? 'landlord removed the unit' : 'other'} · {formatAdminDubaiDate(event.createdAt)}</small>
                )}
            </div>}
        </div>
    )
}

const TestUnitBadge = (): JSX.Element => (
    <span className="badge badge-light-warning border border-warning text-warning bso-statistics__test-unit-badge">Test Unit</span>
)

const splitUnitDetails = (items: Array<Pick<BsoStatisticsUnitDetailItem, 'isDevUnit'>>): { real: number, test: number } =>
    items.reduce((counts, item) => {
        if (item.isDevUnit) {
            counts.test += 1
        } else {
            counts.real += 1
        }
        return counts
    }, { real: 0, test: 0 })

const KpiCard = ({
    title,
    details,
    children,
    onOpen
}: {
    title: string
    details: BsoStatisticsUnitDetailItem[]
    children: React.ReactNode
    onOpen: () => void
}): JSX.Element => {
    const counts = splitUnitDetails(details)
    return (
        <div className="card">
            <span>{title}</span>
            <div className="bso-statistics__kpi-total-line">
                <strong>{counts.real}</strong>
                {counts.test > 0 && <small className="bso-statistics__kpi-test-count">+{counts.test} test</small>}
            </div>
            {children}
            {details.length > 0 && <button className="btn btn-sm btn-light mt-3" type="button" onClick={onOpen}>View units</button>}
        </div>
    )
}

const CompactCountBreakdown = ({ summary, to }: { summary: EntityBreakdownSummary, to?: string }): JSX.Element => {
    const primaryCount = summary.countBreakdown?.real ?? summary.total
    const testCount = summary.countBreakdown?.test ?? 0
    const content = summary.total === 0
        ? <span className="bso-statistics__metric-zero">0</span>
        : (
            <div className="bso-statistics__metric-breakdown">
                <div className="bso-statistics__metric-total-line">
                    <strong className="bso-statistics__metric-total">{primaryCount}</strong>
                    {testCount > 0 && <small className="bso-statistics__metric-test-count">+{testCount} test</small>}
                </div>
                <div className="bso-statistics__metric-groups">
                    {summary.buckets
                        .filter((bucket) => bucket.count > 0)
                        .map((bucket) => (
                            <span className="bso-statistics__metric-group" key={bucket.key}>
                                <span>{bucket.label}</span>
                                <b>{bucket.count}</b>
                            </span>
                        ))}
                </div>
            </div>
        )

    return to != null
        ? <Link className="bso-statistics__metric-link" to={to} state={ACTIVITY_NAV_STATE}>{content}</Link>
        : <>{content}</>
}

const ActivitySummaryCard = ({ title, summary }: { title: string, summary: EntityBreakdownSummary }): JSX.Element => {
    const primaryCount = summary.countBreakdown?.real ?? summary.total
    const testCount = summary.countBreakdown?.test ?? 0

    return (
        <article className="bso-statistics__activity-card">
            <span className="bso-statistics__activity-title">{title}</span>
            <div className="bso-statistics__activity-total-line">
                <strong className="bso-statistics__activity-total">{primaryCount}</strong>
                {testCount > 0 && <small className="bso-statistics__activity-test-count">+{testCount} test</small>}
            </div>
            <div className="bso-statistics__activity-lines">
                {summary.buckets
                    .filter((bucket) => bucket.count > 0)
                    .map((bucket) => (
                        <small key={bucket.key}>
                            <span>{bucket.label}</span>
                            <strong>{bucket.count}</strong>
                        </small>
                    ))}
                {(summary.manualByBsoCount ?? 0) > 0 && (
                    <small>
                        <span>Manual by BSO</span>
                        <strong>{summary.manualByBsoCount}</strong>
                    </small>
                )}
            </div>
        </article>
    )
}

const getUnitReviewError = (response: any): string => {
    const message = response?.response?.error?.message ?? response?.error?.message ?? response?.message
    return typeof message === 'string' && message.trim() !== '' ? message : 'Could not save Unit Review.'
}

const getDubaiDateKey = (value: string | Date): string | null => {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return null
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Dubai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(date)
    const year = parts.find((part) => part.type === 'year')?.value
    const month = parts.find((part) => part.type === 'month')?.value
    const day = parts.find((part) => part.type === 'day')?.value
    return year != null && month != null && day != null ? `${year}-${month}-${day}` : null
}

const getOccupiedUntilAlert = (row: Pick<BsoUnitAnalyticsRow, 'conditionStatus' | 'marketStatus' | 'occupiedUntil'>, todayDubaiKey: string): OccupiedUntilAlert | null => {
    if (row.conditionStatus !== 'Occupied' || row.marketStatus !== 'Active' || row.occupiedUntil == null) return null
    const occupiedUntilKey = getDubaiDateKey(row.occupiedUntil)
    if (occupiedUntilKey == null || occupiedUntilKey > todayDubaiKey) return null
    return occupiedUntilKey === todayDubaiKey
        ? { label: 'Occupied ends today', tone: 'today' }
        : { label: 'Occupied expired', tone: 'expired' }
}

const OccupiedUntilAlertBadge = ({ alert }: { alert: OccupiedUntilAlert }): JSX.Element => (
    <span className={`badge bso-statistics__occupied-alert bso-statistics__occupied-alert--${alert.tone}`} title="Update unit status">
        {alert.label}
    </span>
)

const DetailStatusChips = ({ item }: { item: BsoStatisticsUnitDetailItem }): JSX.Element => {
    const chips = [
        ...(item.conditionStatus != null ? [item.conditionStatus] : []),
        ...item.accessStatuses,
        ...item.statuses.filter((status) => status !== item.conditionStatus && !item.accessStatuses.includes(status))
    ]

    if (chips.length === 0) return <EmptyValue />

    return (
        <div className="bso-statistics__analytics-chips">
            {chips.slice(0, 8).map((chip) => (
                <StatusBadgeWithMeta occupiedUntil={item.occupiedUntil} status={chip} key={chip} />
            ))}
        </div>
    )
}

const BsoStatisticsDetailsOverlay = ({
    state,
    onClose,
    analyticsRowsByUnitId
}: {
    state: DetailsOverlayState,
    onClose: () => void,
    analyticsRowsByUnitId: Map<number, BsoUnitAnalyticsRow>
}): JSX.Element | null => {
    const navigate = useNavigate()
    React.useEffect(() => {
        if (state == null) return
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [state, onClose])

    if (state == null || typeof document === 'undefined') return null

    const openUnitRoute = (unitRoute: string | null): void => {
        if (unitRoute != null) navigate(unitRoute, { state: ACTIVITY_NAV_STATE })
    }

    const handleRowClick = (event: React.MouseEvent<HTMLElement>, unitRoute: string | null): void => {
        const target = event.target instanceof HTMLElement ? event.target : null
        if (target?.closest('a,button') != null) return
        openUnitRoute(unitRoute)
    }

    const handleRowKeyDown = (event: React.KeyboardEvent<HTMLElement>, unitRoute: string | null): void => {
        const target = event.target instanceof HTMLElement ? event.target : null
        if (target?.closest('a,button') != null) return
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openUnitRoute(unitRoute)
        }
    }

    return createPortal(
        <div className="bso-statistics__overlay" onClick={onClose}>
            <div className="bso-statistics__overlay-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
                <header className="bso-statistics__overlay-header">
                    <div>
                        <h2>{state.title}</h2>
                        <p>{state.subtitle}</p>
                    </div>
                    <button className="btn btn-sm btn-light" type="button" onClick={onClose} aria-label="Close">Close</button>
                </header>
                <div className="bso-statistics__overlay-list">
                    <div className="bso-statistics__overlay-table-wrap">
                        <table className="table align-middle mb-0 bso-statistics__analytics-table bso-statistics__overlay-analytics-table">
                            <thead>
                                <tr className="text-muted fw-semibold">
                                    <th>Unit / Property</th>
                                    <th>Market Status</th>
                                    <th>Status</th>
                                    <th>Unit Review</th>
                                    <th>Publication Lifecycle</th>
                                    <th>Initial Price</th>
                                    <th>Price Drop</th>
                                    <th>Ads</th>
                                    <th>Viewings</th>
                                    <th>Offers</th>
                                </tr>
                            </thead>
                            <tbody>
                                {state.items.map((item) => {
                                    const activityRoute = Number.isFinite(item.unitId) ? `/landlord/my_units/statistics/${String(item.unitId)}` : null
                                    const isClickable = activityRoute != null
                                    const analyticsRow = analyticsRowsByUnitId.get(item.unitId)
                                    return (
                                        <tr
                                            aria-label={isClickable ? `Open unit ${item.unitReference}` : undefined}
                                            className={isClickable ? 'bso-statistics__overlay-row--clickable' : undefined}
                                            key={`${item.unitId}-${String(item.offerId ?? '')}`}
                                            role={isClickable ? 'link' : undefined}
                                            tabIndex={isClickable ? 0 : undefined}
                                            onClick={isClickable ? (event) => handleRowClick(event, activityRoute) : undefined}
                                            onKeyDown={isClickable ? (event) => handleRowKeyDown(event, activityRoute) : undefined}
                                        >
                                            <td className="bso-statistics__analytics-unit">
                                                <img className="bso-statistics__analytics-unit-image" src={item.imageUrl} alt={item.headline} />
                                                <div className="bso-statistics__analytics-unit-content">
                                                    <div className="bso-statistics__analytics-unit-head">
                                                        <div className="bso-statistics__analytics-unit-title" title={item.headline}>
                                                            {item.headline}
                                                        </div>
                                                    </div>
                                                    <div className="bso-statistics__analytics-unit-meta">
                                                        <span>{item.propertyName}</span>
                                                        <small>{item.areaName}{item.unitNumber != null ? `, Unit ${item.unitNumber}` : `, Ref ${item.unitReference}`}</small>
                                                        {(item.unitType != null || item.unitCategory != null || item.sizeArea != null || item.roomsLabel != null) && (
                                                            <div className="bso-statistics__analytics-unit-tags">
                                                                {item.unitType != null && <span className="badge badge-light">{item.unitType}</span>}
                                                                {item.unitCategory != null && <span className="badge badge-light">{item.unitCategory}</span>}
                                                                {item.sizeArea != null && <span className="badge badge-light">{item.sizeArea.toLocaleString('en-US')} sqft</span>}
                                                                {item.roomsLabel != null && <span className="badge badge-light">{item.roomsLabel}</span>}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="bso-statistics__analytics-unit-controls">
                                                    {item.listingType != null && <span className={`badge ${listingTypeClassName[item.listingType]} bso-statistics__analytics-listing-badge`}>{item.listingType}</span>}
                                                    {item.isDevUnit && <TestUnitBadge />}
                                                </div>
                                            </td>
                                            <td><MarketStatusBadge status={item.marketStatus} /></td>
                                            <td><DetailStatusChips item={item} /></td>
                                            <td><UnitReviewCell unitReview={item.unitReview} activity={[]} /></td>
                                            <td><ListingTimelineCell row={item} /></td>
                                            <td>
                                                {formatAnalyticsPrice(item.initialPrice)}
                                                {item.initialPriceSource === 'current-price-fallback' && (
                                                    <small className="text-muted d-block">first known</small>
                                                )}
                                            </td>
                                            <td>{formatPriceDrop(item)}</td>
                                            <td>{analyticsRow != null ? <CompactCountBreakdown summary={analyticsRow.adsSummary} /> : <EmptyValue />}</td>
                                            <td>{analyticsRow != null ? <CompactCountBreakdown summary={analyticsRow.viewingsSummary} /> : <EmptyValue />}</td>
                                            <td>{analyticsRow != null ? <CompactCountBreakdown summary={analyticsRow.offersSummary} /> : <EmptyValue />}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}

const PeriodRows = ({ periods, onOpenPeriod }: { periods: StatisticPeriod[], onOpenPeriod: (period: StatisticPeriod) => void }): JSX.Element => (
    <div className="table-responsive bso-statistics__period-table-wrap">
        <table className="table align-middle mb-0 bso-statistics__period-table">
            <thead>
                <tr className="text-muted fw-semibold">
                    <th>Period</th>
                    <th>Count</th>
                    <th>Avg. days listed</th>
                    <th>Details</th>
                </tr>
            </thead>
            <tbody>
                {periods.map((period) => (
                    <React.Fragment key={period.id}>
                        <tr>
                            <td className="fw-bold">{period.label}</td>
                            <td>{period.count}</td>
                            <td>{period.averageDaysPublished ?? <EmptyValue />}</td>
                            <td>
                                {period.events.length > 0
                                    ? (
                                        <button
                                            className="btn btn-sm btn-light bso-statistics__period-details-button"
                                            type="button"
                                            onClick={() => onOpenPeriod(period)}
                                        >
                                            View details
                                        </button>
                                    )
                                    : <EmptyValue />}
                            </td>
                        </tr>
                    </React.Fragment>
                ))}
            </tbody>
        </table>
    </div>
)

const formatAnalyticsPrice = (value: number | null): JSX.Element | string =>
    value != null ? `AED ${value.toLocaleString('en-US')}` : <EmptyValue />

const formatPriceDrop = (row: Pick<BsoUnitAnalyticsRow, 'priceDrop' | 'currentPrice'>): JSX.Element | string => {
    if (row.priceDrop == null || row.currentPrice == null) return <EmptyValue />
    return (
        <>
            {formatAnalyticsPrice(row.priceDrop)}
            <small className="text-muted d-block">Now {formatAnalyticsPrice(row.currentPrice)}</small>
        </>
    )
}

type ListingTimelineRow = Pick<BsoUnitAnalyticsRow, 'marketStatus' | 'catalogDate' | 'endDate' | 'daysPublished' | 'offerId' | 'offerRoute'> & {
    endReason?: string | null
    processStartDate?: string | null
    processEndDate?: string | null
    isProcessEndDateInferred?: boolean
}

const marketStatusClassName: Record<NonNullable<BsoUnitAnalyticsRow['marketStatus']>, string> = {
    Active: 'badge-light-warning',
    Hidden: 'badge-light',
    Archived: 'badge-light',
    'Off Market': 'badge-light-success'
}

const listingTypeClassName: Record<NonNullable<BsoUnitAnalyticsRow['listingType']>, string> = {
    Rent: 'badge-light-info border border-info text-info',
    Sale: 'badge-light-warning border border-warning text-warning'
}

const MarketStatusBadge = ({ status }: { status: BsoUnitAnalyticsRow['marketStatus'] }): JSX.Element => (
    status != null
        ? <span className={`badge ${marketStatusClassName[status]}`}>{status}</span>
        : <EmptyValue />
)

const UnitStatusChips = ({ row, occupiedAlert }: { row: BsoUnitAnalyticsRow, occupiedAlert?: OccupiedUntilAlert | null }): JSX.Element => {
    const chips = [
        ...(row.conditionStatus != null ? [row.conditionStatus] : []),
        ...row.accessStatuses
    ]

    if (chips.length === 0 && occupiedAlert == null) return <EmptyValue />
    return (
        <div className="bso-statistics__analytics-chips">
            {chips.map((chip) => (
                <StatusBadgeWithMeta occupiedUntil={row.occupiedUntil} status={chip} key={chip} />
            ))}
            {occupiedAlert != null && <OccupiedUntilAlertBadge alert={occupiedAlert} />}
        </div>
    )
}

const ListingTimelineCell = ({ row }: { row: ListingTimelineRow }): JSX.Element => {
    const isPublished = row.marketStatus === 'Active'
    const isEnded = row.marketStatus === 'Off Market' || row.marketStatus === 'Archived'

    if (isPublished) {
        return (
            <div className="bso-statistics__timeline-cell">
                <span className="bso-statistics__timeline-label">Start / End</span>
                <strong className="bso-statistics__timeline-range">
                    {row.catalogDate != null ? formatAdminDubaiDate(row.catalogDate) : '-'}
                    <span aria-hidden="true">&rarr;</span>
                    <em>Still published</em>
                </strong>
                {row.daysPublished != null && (
                    <span className="bso-statistics__timeline-duration">
                        <span className="badge badge-light bso-statistics__timeline-days">{row.daysPublished}</span>
                        days published
                    </span>
                )}
            </div>
        )
    }

    if (!isEnded) {
        return (
            <div className="bso-statistics__timeline-cell">
                <span className="bso-statistics__timeline-label">Start / End</span>
                <strong className="bso-statistics__timeline-range">
                    {row.catalogDate != null ? formatAdminDubaiDate(row.catalogDate) : '-'}
                    <span aria-hidden="true">&rarr;</span>
                    <em>-</em>
                </strong>
                {row.daysPublished != null && (
                    <span className="bso-statistics__timeline-duration">
                        <span className="badge badge-light bso-statistics__timeline-days">{row.daysPublished}</span>
                        days published
                    </span>
                )}
            </div>
        )
    }

    return (
        <div className="bso-statistics__timeline-cell">
            <span className="bso-statistics__timeline-label">Start / End</span>
            <strong className="bso-statistics__timeline-range">
                {row.catalogDate != null ? formatAdminDubaiDate(row.catalogDate) : '-'}
                <span aria-hidden="true">&rarr;</span>
                {row.endDate != null ? formatAdminDubaiDate(row.endDate) : '-'}
            </strong>
            {row.daysPublished != null && (
                <span className="bso-statistics__timeline-duration">
                    <span className="badge badge-light bso-statistics__timeline-days">{row.daysPublished}</span>
                    days published
                </span>
            )}
            {row.offerId != null
                ? (
                    row.offerRoute != null
                        ? (
                            <Link to={row.offerRoute}>
                                Completed offer #{row.offerId}
                            </Link>
                        )
                        : <span>Completed offer #{row.offerId}</span>
                )
                : row.endReason != null && <small>{row.endReason}</small>}
            {row.processStartDate !== undefined && (
                <small>Process start: {formatAdminDubaiDate(row.processStartDate)}</small>
            )}
            {row.processEndDate !== undefined && (
                <small>
                    Process end: {formatAdminDubaiDate(row.processEndDate)}
                    {row.isProcessEndDateInferred ? ' (inferred)' : ''}
                </small>
            )}
            {row.offerId != null && row.endReason != null && !row.endReason.includes(`#${String(row.offerId)}`) && <small>{row.endReason}</small>}
        </div>
    )
}

const QUICK_ACTIONS = ['Add Viewing', 'Add Offer', 'Add Advertisement', 'Add Unit Review'] as const

const UnitQuickActions = ({
    row,
    onOpenUnitReview,
    onOpenManualViewing,
    onOpenManualOffer,
    onOpenManualAdvertisement
}: {
    row: BsoUnitAnalyticsRow
    onOpenUnitReview: (row: BsoUnitAnalyticsRow) => void
    onOpenManualViewing: (row: BsoUnitAnalyticsRow) => void
    onOpenManualOffer: (row: BsoUnitAnalyticsRow) => void
    onOpenManualAdvertisement: (row: BsoUnitAnalyticsRow) => void
}): JSX.Element => {
    const triggerRef = React.useRef<HTMLButtonElement | null>(null)
    const menuRef = React.useRef<HTMLDivElement | null>(null)
    const [open, setOpen] = React.useState(false)
    const [position, setPosition] = React.useState<{ top: number, left: number } | null>(null)
    const closeMenu = React.useCallback((): void => { setOpen(false) }, [])
    const updatePosition = React.useCallback((): void => {
        const rect = triggerRef.current?.getBoundingClientRect()
        if (rect == null) return
        const height = 176
        const up = window.innerHeight - rect.bottom < height && rect.top > height
        setPosition({ top: up ? rect.top - height : rect.bottom + 4, left: Math.max(8, rect.right - 190) })
    }, [])

    React.useEffect(() => {
        if (!open) return
        updatePosition()
        const outside = (event: MouseEvent): void => { if (!triggerRef.current?.contains(event.target as Node) && !menuRef.current?.contains(event.target as Node)) closeMenu() }
        const escape = (event: KeyboardEvent): void => { if (event.key === 'Escape') closeMenu() }
        window.addEventListener('resize', updatePosition)
        window.addEventListener('scroll', updatePosition, true)
        document.addEventListener('mousedown', outside)
        document.addEventListener('keydown', escape)
        return () => {
            window.removeEventListener('resize', updatePosition)
            window.removeEventListener('scroll', updatePosition, true)
            document.removeEventListener('mousedown', outside)
            document.removeEventListener('keydown', escape)
        }
    }, [closeMenu, open, updatePosition])

    const handleActionClick = (action: typeof QUICK_ACTIONS[number]): void => {
        closeMenu()
        if (action === 'Add Viewing') {
            onOpenManualViewing(row)
            return
        }
        if (action === 'Add Offer') {
            onOpenManualOffer(row)
            return
        }
        if (action === 'Add Advertisement') {
            onOpenManualAdvertisement(row)
            return
        }
        if (action === 'Add Unit Review') {
            onOpenUnitReview(row)
            return
        }
        console.log('[Units Analytics] quick action TODO', { action, unitId: row.unitId })
    }

    const editUnitRoute = row.propertyId != null
        ? `/landlord/set_unit_modal/edit_unit_modal/${String(row.unitId)}/${String(row.propertyId)}`
        : null

    return (
        <div className="bso-statistics__analytics-actions">
            <button ref={triggerRef} type="button" aria-label={`Open actions for ${row.headline}`} aria-expanded={open} onClick={() => setOpen((value) => !value)}>
                Actions <span aria-hidden="true">▾</span>
            </button>
            {open && position != null && typeof document !== 'undefined' && createPortal(<div ref={menuRef} className="bso-statistics__analytics-actions-menu" style={{ position: 'fixed', top: position.top, left: position.left, zIndex: 10000 }}>
                {QUICK_ACTIONS.map((action) => (
                    <button key={action} type="button" onClick={() => handleActionClick(action)}>
                        {action === 'Add Unit Review' && row.unitReview != null ? 'Edit Unit Review' : action}
                    </button>
                ))}
                {editUnitRoute != null && (
                    <Link to={editUnitRoute} onClick={closeMenu}>
                        Edit Unit
                    </Link>
                )}
            </div>, document.body)}
        </div>
    )
}

const UnitReviewModal = ({
    state,
    onClose,
    onReasonsChange,
    onSave
}: {
    state: UnitReviewModalState
    onClose: () => void
    onReasonsChange: (category: UnitReviewCategory, reasons: string[]) => void
    onSave: () => void
}): JSX.Element | null => {
    const unitReviewValue = useAppSelector((root: RootState) => root.units.units.find((unit) => unit.id === state?.row.unitId)?.attributes?.UnitReview)
    const unitReviewOptionsConfig = useAppSelector((root: RootState) => root.app.config?.UnitReviewOptions)
    React.useEffect(() => {
        if (state == null) return
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape' && !state.saving) onClose()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [state, onClose])

    if (state == null || typeof document === 'undefined') return null

    const activityEvents = getUnitReviewActivityEvents(unitReviewValue)
    const getPopupContainer = (triggerNode: HTMLElement): HTMLElement =>
        triggerNode.closest('.bso-statistics__review-modal-backdrop') ?? document.body

    return createPortal(
        <div className="bso-statistics__review-modal-backdrop" onClick={state.saving ? undefined : onClose}>
            <div className="bso-statistics__review-modal" role="dialog" aria-modal="true" aria-labelledby="unit-review-title" onClick={(event) => event.stopPropagation()}>
                <header className="bso-statistics__review-modal-header">
                    <div>
                        <h2 id="unit-review-title">Unit Review</h2>
                        <p>{state.row.unitReference} - {state.row.propertyName}</p>
                    </div>
                    <button className="btn btn-sm btn-light" type="button" onClick={onClose} disabled={state.saving}>Close</button>
                </header>
                <div className="bso-statistics__review-modal-body">
                    <label className="form-label">Temporary Problems</label>
                    <Select
                        className="w-100 mb-3"
                        mode="multiple"
                        value={state.temporaryReasons.map((reason) => reason.value)}
                        options={getUnitReviewSelectOptions('temporary', unitReviewOptionsConfig, state.temporaryReasons)}
                        onChange={(reasons) => onReasonsChange('temporary', reasons)}
                        getPopupContainer={getPopupContainer}
                        disabled={state.saving}
                    />

                    <label className="form-label">Permanent Problems</label>
                    <Select
                        className="w-100"
                        mode="multiple"
                        value={state.permanentReasons.map((reason) => reason.value)}
                        options={getUnitReviewSelectOptions('permanent', unitReviewOptionsConfig, state.permanentReasons)}
                        onChange={(reasons) => onReasonsChange('permanent', reasons)}
                        getPopupContainer={getPopupContainer}
                        disabled={state.saving}
                    />

                    {activityEvents.length > 0 && <>
                        <label className="form-label mt-4">Unit activity</label>
                        <div className="border rounded p-2 small">
                            {activityEvents.map((event, index) => (
                                <div key={`${event.createdAt}-${String(index)}`} className="mb-2">
                                    {event.type === 'relisted'
                                        ? <Link to={`/landlord/modalView/unit_modal/${String(getRelatedRelistUnitId(event))}/not/catalog`}>Relisted {event.direction === 'to' ? 'to' : 'from'} Unit #{String(getRelatedRelistUnitId(event))}</Link>
                                        : <strong>{event.reason === 'other_offer_completed' ? 'Unit is off market — other offer completed' : 'Unit is off market — landlord removed the unit'}</strong>}
                                    <br />{formatAdminDubaiDate(event.createdAt)}
                                    {event.type === 'off_market' && event.offerId != null && <> · Offer #{String(event.offerId)}</>}
                                    {event.type === 'off_market' && event.outcome != null && <> · {event.outcome}</>}
                                </div>
                            ))}
                        </div>
                    </>}

                    {state.error != null && <div className="alert alert-light-danger mt-3 mb-0">{state.error}</div>}
                </div>
                <footer className="bso-statistics__review-modal-footer">
                    <button className="btn btn-sm btn-light" type="button" onClick={onClose} disabled={state.saving}>Cancel</button>
                    <button className="btn btn-sm btn-primary" type="button" onClick={onSave} disabled={state.saving}>
                        {state.saving ? 'Saving...' : 'Save'}
                    </button>
                </footer>
            </div>
        </div>,
        document.body
    )
}

const UnitAdvertisementPortalLinks = ({ row, advertises, users }: { row: BsoUnitAnalyticsRow, advertises: any[], users: any[] }): JSX.Element => {
    const uniqueAdvertises: any[] = Array.from(
        advertises
            .filter((advertise) => getRelatedUnitId(advertise) === row.unitId)
            .reduce((map, advertise) => {
                const advertiseId = Number(advertise?.id)
                if (Number.isFinite(advertiseId) && !map.has(advertiseId)) {
                    map.set(advertiseId, advertise)
                }
                return map
            }, new Map<number, any>())
            .values()
    )
    const groups = uniqueAdvertises
        .filter((advertise) => getRelatedUnitId(advertise) === row.unitId)
        .map((advertise) => ({
            advertiseId: advertise.id,
            agent: getAgentDisplay(advertise, users),
            isManualByBso: isManualBsoAction(advertise),
            links: getAdvertisementLinks(advertise)
        }))
        .filter((group) => group.links.length > 0)

    if (groups.length === 0) return <EmptyValue />


    return (
        <div className="bso-statistics__portal-links">
            <strong className="bso-statistics__portal-links-count">{groups.length}</strong>
            {groups.slice(0, 4).map((group) => (
                <div className="bso-statistics__portal-link-group" key={String(group.advertiseId)}>
                    <div className="bso-statistics__portal-link-agent">
                        <span aria-hidden="true" />
                        <small title={group.agent.name}>{group.agent.name}</small>
                        {group.isManualByBso && <em className="bso-statistics__manual-bso-badge">Manual by BSO</em>}
                        <b>{group.links.length}</b>
                    </div>
                    <small className="bso-statistics__portal-link-ad-id">Ad #{String(group.advertiseId)}</small>
                    {group.links.slice(0, 4).map((link, index) => (
                        <div className="bso-statistics__portal-link" key={`${String(group.advertiseId)}-${String(index)}-${link.label}-${String(link.trakheesiId ?? '')}`}>
                            {link.trakheesiId != null
                                ? (
                                    <span className="bso-statistics__portal-trakheesi">
                                        <small>Trakheesi</small>
                                        <i>{link.trakheesiId}</i>
                                    </span>
                                )
                                : link.url != null
                                    ? <a href={link.url} target="_blank" rel="noreferrer">Link: {link.label}</a>
                                    : <span>Link: {link.label}</span>}
                        </div>
                    ))}
                    {group.links.length > 4 && <em>+{group.links.length - 4} more</em>}
                </div>
            ))}
            {groups.length > 4 && <small className="text-muted">+{groups.length - 4} more advertisement(s)</small>}
        </div>
    )
}

const AnalyticsPagination = ({
    currentPage,
    totalPages,
    totalRows,
    onPageChange
}: {
    currentPage: number
    totalPages: number
    totalRows: number
    onPageChange: (page: number) => void
}): JSX.Element | null => {
    if (totalPages <= 1) return null
    const pages = Array.from(new Set([
        1,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        totalPages
    ].filter((page) => page >= 1 && page <= totalPages)))

    return (
        <div className="bso-statistics__analytics-pagination" aria-label="Units analytics pagination">
            <span>{totalRows.toLocaleString('en-US')} units</span>
            <button className="btn btn-sm btn-light" type="button" disabled={currentPage <= 1} onClick={() => onPageChange(currentPage - 1)}>
                Previous
            </button>
            <div className="bso-statistics__analytics-pages">
                {pages.map((page, index) => (
                    <React.Fragment key={page}>
                        {index > 0 && page - pages[index - 1] > 1 && <small>...</small>}
                        <button
                            className={`btn btn-sm ${page === currentPage ? 'btn-dark' : 'btn-light'}`}
                            type="button"
                            aria-current={page === currentPage ? 'page' : undefined}
                            onClick={() => onPageChange(page)}
                        >
                            {page}
                        </button>
                    </React.Fragment>
                ))}
            </div>
            <button className="btn btn-sm btn-light" type="button" disabled={currentPage >= totalPages} onClick={() => onPageChange(currentPage + 1)}>
                Next
            </button>
            <span>Page {currentPage} of {totalPages}</span>
        </div>
    )
}

const UnitAnalyticsTable = ({
    rows,
    totalRows,
    currentPage,
    totalPages,
    occupiedAlertByUnitId,
    advertises,
    users,
    isLoading,
    error,
    onOpenUnitReview,
    onOpenManualViewing,
    onOpenManualOffer,
    onOpenManualAdvertisement,
    onPageChange
}: {
    rows: BsoUnitAnalyticsRow[]
    totalRows: number
    currentPage: number
    totalPages: number
    occupiedAlertByUnitId: Map<number, OccupiedUntilAlert>
    advertises: any[]
    users: any[]
    isLoading: boolean
    error: string | null
    onOpenUnitReview: (row: BsoUnitAnalyticsRow) => void
    onOpenManualViewing: (row: BsoUnitAnalyticsRow) => void
    onOpenManualOffer: (row: BsoUnitAnalyticsRow) => void
    onOpenManualAdvertisement: (row: BsoUnitAnalyticsRow) => void
    onPageChange: (page: number) => void
}): JSX.Element => {
    if (totalRows === 0 && isLoading) {
        return <div className="alert alert-light mb-0">Loading units analytics...</div>
    }

    if (totalRows === 0) {
        return <div className="alert alert-light mb-0">No units available for analytics.</div>
    }

    return (
        <>
            {error != null && (
                <div className="alert alert-light mb-3">
                    Some analytics data could not be loaded: {error}
                </div>
            )}
            {isLoading && <small className="text-muted d-block mb-2">Updating analytics data...</small>}
            <div className="bso-statistics__analytics-table-wrap">
                <table className="table align-middle mb-0 bso-statistics__analytics-table">
                    <thead>
                        <tr className="text-muted fw-semibold">
                            <th>Unit / Property</th>
                            <th>Market Status</th>
                            <th>Status</th>
                            <th>Unit Review</th>
                            <th>Publication Lifecycle</th>
                            <th>Initial Price</th>
                            <th>Price Drop</th>
                            <th>Ads</th>
                            <th>Portal Links</th>
                            <th>Viewings</th>
                            <th>Offers</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            const activityRoute = `/landlord/my_units/statistics/${String(row.unitId)}`
                            return (
                                <tr key={row.unitId}>
                                    <td className="bso-statistics__analytics-unit">
                                        <Link className="bso-statistics__analytics-unit-image-link" to={activityRoute} state={ACTIVITY_NAV_STATE} aria-label={`Open activity for ${row.headline}`}>
                                            <img className="bso-statistics__analytics-unit-image" src={row.imageUrl} alt={row.headline} />
                                        </Link>
                                        <div className="bso-statistics__analytics-unit-content">
                                            <div className="bso-statistics__analytics-unit-head">
                                                <Link className="bso-statistics__analytics-unit-title" to={activityRoute} state={ACTIVITY_NAV_STATE} title={row.headline}>
                                                    {row.headline}
                                                </Link>
                                            </div>
                                            <div className="bso-statistics__analytics-unit-meta">
                                                <span>{row.propertyName}</span>
                                                <small>{row.areaName}{row.unitNumber != null ? `, Unit ${row.unitNumber}` : `, Ref ${row.unitReference}`}</small>
                                                {row.erpUnitId != null && <small>ERP Unit ID: {row.erpUnitId}</small>}
                                                {(row.unitType != null || row.unitCategory != null || row.sizeArea != null || row.roomsLabel != null) && (
                                                    <div className="bso-statistics__analytics-unit-tags">
                                                        {row.unitType != null && <span className="badge badge-light">{row.unitType}</span>}
                                                        {row.unitCategory != null && <span className="badge badge-light">{row.unitCategory}</span>}
                                                        {row.sizeArea != null && <span className="badge badge-light">{row.sizeArea.toLocaleString('en-US')} sqft</span>}
                                                        {row.roomsLabel != null && <span className="badge badge-light">{row.roomsLabel}</span>}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="bso-statistics__analytics-unit-controls">
                                            {row.listingType != null && <span className={`badge ${listingTypeClassName[row.listingType]} bso-statistics__analytics-listing-badge`}>{row.listingType}</span>}
                                            {row.isDevUnit && <TestUnitBadge />}
                                            <UnitQuickActions row={row} onOpenUnitReview={onOpenUnitReview} onOpenManualViewing={onOpenManualViewing} onOpenManualOffer={onOpenManualOffer} onOpenManualAdvertisement={onOpenManualAdvertisement} />
                                        </div>
                                    </td>
                                    <td><MarketStatusBadge status={row.marketStatus} /></td>
                                    <td><UnitStatusChips row={row} occupiedAlert={occupiedAlertByUnitId.get(row.unitId)} /></td>
                                    <td><UnitReviewCell unitReview={row.unitReview} activity={row.unitActivity} /></td>
                                    <td><ListingTimelineCell row={row} /></td>
                                    <td>
                                        {formatAnalyticsPrice(row.initialPrice)}
                                        {row.initialPriceSource === 'current-price-fallback' && (
                                            <small className="text-muted d-block">first known</small>
                                        )}
                                    </td>
                                    <td>{formatPriceDrop(row)}</td>
                                    <td><CompactCountBreakdown summary={row.adsSummary} to={`${activityRoute}?tab=advertisements`} /></td>
                                    <td><UnitAdvertisementPortalLinks row={row} advertises={advertises} users={users} /></td>
                                    <td><CompactCountBreakdown summary={row.viewingsSummary} to={`${activityRoute}?tab=viewings`} /></td>
                                    <td><CompactCountBreakdown summary={row.offersSummary} to={`${activityRoute}?tab=offers`} /></td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            <AnalyticsPagination currentPage={currentPage} totalPages={totalPages} totalRows={totalRows} onPageChange={onPageChange} />
        </>
    )
}

export default function AdminBsoUnitStatisticsPage(): JSX.Element {
    const dispatch = useAppDispatch()
    const [searchParams, setSearchParams] = useSearchParams()
    const units = useAppSelector((state: RootState) => state.units.units)
    const offers = useAppSelector((state: RootState) => state.offers.offers)
    const viewings = useAppSelector((state: RootState) => state.viewings.viewings)
    const advertises = useAppSelector((state: RootState) => state.advertises.advertises)
    const users = useAppSelector((state: RootState) => state.user.allUsers)
    const currentUser = useAppSelector((state: RootState) => state.user.currentUser as { id?: number | string })
    const authUserId = useAppSelector((state: RootState) => state.auth.userID)
    const analyticsLoading = useAppSelector((state: RootState) => state.units.unitsLoading || state.offers.offersLoading || state.viewings.viewingsLoading || state.advertises.advertisesLoading)
    const analyticsError = useAppSelector((state: RootState) => {
        const errors = [state.units.error, state.offers.error, state.viewings.error, state.advertises.error]
            .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
        return errors.length > 0 ? errors.join('; ') : null
    })
    const statistics = React.useMemo(() => buildBsoUnitStatistics(units, offers, users), [units, offers, users])
    const analyticsRows = React.useMemo(() => buildBsoUnitAnalyticsRows(units, offers, viewings, advertises), [units, offers, viewings, advertises])
    const activitySummary = React.useMemo(() => buildBsoActivitySummary(analyticsRows), [analyticsRows])
    const analyticsRowsByUnitId = React.useMemo(() => {
        return new Map(analyticsRows.map((row) => [row.unitId, row]))
    }, [analyticsRows])
    const [analyticsSearch, setAnalyticsSearch] = React.useState('')
    const [marketStatusFilter, setMarketStatusFilter] = React.useState<MarketStatusFilter>('all')
    const [listingTypeFilter, setListingTypeFilter] = React.useState<ListingTypeFilter>('all')
    const [analyticsSort, setAnalyticsSort] = React.useState<AnalyticsSort>('newest')
    const [unitReviewModal, setUnitReviewModal] = React.useState<UnitReviewModalState>(null)
    const [manualViewingUnit, setManualViewingUnit] = React.useState<BsoUnitAnalyticsRow | null>(null)
    const [manualOfferUnit, setManualOfferUnit] = React.useState<BsoUnitAnalyticsRow | null>(null)
    const [manualAdvertisementUnit, setManualAdvertisementUnit] = React.useState<BsoUnitAnalyticsRow | null>(null)
    const requestedAnalyticsPage = parsePositiveInt(searchParams.get(ANALYTICS_PAGE_QUERY_PARAM))
    const filteredAnalyticsRows = React.useMemo(() => {
        const normalizedSearch = analyticsSearch.trim().toLowerCase()
        const rows = analyticsRows.filter((row) => {
            const matchesSearch = normalizedSearch === '' || [
                row.headline,
                row.propertyName,
                row.areaName,
                row.unitNumber,
                row.unitReference,
                row.erpUnitId
            ].some((value) => value?.toLowerCase().includes(normalizedSearch) === true)
            const matchesMarketStatus =
                marketStatusFilter === 'all' ||
                (marketStatusFilter === 'active' && row.marketStatus === 'Active') ||
                (marketStatusFilter === 'off-market' && row.marketStatus === 'Off Market')
            const matchesListingType =
                listingTypeFilter === 'all' ||
                (listingTypeFilter === 'rent' && row.listingType === 'Rent') ||
                (listingTypeFilter === 'sale' && row.listingType === 'Sale')
            return matchesSearch && matchesMarketStatus && matchesListingType
        })
        const order = (left: BsoUnitAnalyticsRow, right: BsoUnitAnalyticsRow): number => {
            if (analyticsSort === 'price-desc' || analyticsSort === 'price-asc') {
                const leftPrice = left.currentPrice
                const rightPrice = right.currentPrice
                if (leftPrice == null && rightPrice == null) return 0
                if (leftPrice == null) return 1
                if (rightPrice == null) return -1
                return analyticsSort === 'price-desc' ? rightPrice - leftPrice : leftPrice - rightPrice
            }
            const leftDate = new Date(left.createdAt ?? 0).getTime()
            const rightDate = new Date(right.createdAt ?? 0).getTime()
            return analyticsSort === 'newest' ? rightDate - leftDate : leftDate - rightDate
        }
        const sorted = [...rows].sort(order)
        if (analyticsSort !== 'newest') return sorted
        const byId = new Map(sorted.map((row) => [row.unitId, row]))
        const emitted = new Set<number>()
        return sorted.reduce<BsoUnitAnalyticsRow[]>((grouped, row) => {
            if (emitted.has(row.unitId)) return grouped
            grouped.push(row)
            emitted.add(row.unitId)
            row.unitActivity.filter((event): event is RelistActivityEvent => event.type === 'relisted').forEach((event) => {
                const related = byId.get(getRelatedRelistUnitId(event))
                if (related != null && !emitted.has(related.unitId)) {
                    grouped.push(related)
                    emitted.add(related.unitId)
                }
            })
            return grouped
        }, [])
    }, [analyticsRows, analyticsSearch, analyticsSort, listingTypeFilter, marketStatusFilter])
    const analyticsTotalPages = Math.max(1, Math.ceil(filteredAnalyticsRows.length / ANALYTICS_PAGE_SIZE))
    const analyticsPage = Math.min(requestedAnalyticsPage, analyticsTotalPages)
    const paginatedAnalyticsRows = React.useMemo(() => {
        const startIndex = (analyticsPage - 1) * ANALYTICS_PAGE_SIZE
        return filteredAnalyticsRows.slice(startIndex, startIndex + ANALYTICS_PAGE_SIZE)
    }, [analyticsPage, filteredAnalyticsRows])
    const setAnalyticsPage = React.useCallback((page: number, replace = false) => {
        const nextPage = Math.max(1, Math.floor(page))
        const nextSearchParams = new URLSearchParams(searchParams)
        if (nextPage <= 1) {
            nextSearchParams.delete(ANALYTICS_PAGE_QUERY_PARAM)
        } else {
            nextSearchParams.set(ANALYTICS_PAGE_QUERY_PARAM, String(nextPage))
        }
        setSearchParams(nextSearchParams, { replace })
    }, [searchParams, setSearchParams])
    React.useEffect(() => {
        if (requestedAnalyticsPage !== analyticsPage) {
            setAnalyticsPage(analyticsPage, true)
        }
    }, [analyticsPage, requestedAnalyticsPage, setAnalyticsPage])
    const todayDubaiKey = React.useMemo(() => getDubaiDateKey(new Date()) ?? '', [])
    const occupiedAlertByUnitId = React.useMemo(() => {
        const alerts = new Map<number, OccupiedUntilAlert>()
        analyticsRows.forEach((row) => {
            const alert = getOccupiedUntilAlert(row, todayDubaiKey)
            if (alert != null) alerts.set(row.unitId, alert)
        })
        return alerts
    }, [analyticsRows, todayDubaiKey])
    const filteredOccupiedAlertCount = React.useMemo(() => {
        return filteredAnalyticsRows.filter((row) => occupiedAlertByUnitId.has(row.unitId)).length
    }, [filteredAnalyticsRows, occupiedAlertByUnitId])
    const [detailsOverlay, setDetailsOverlay] = React.useState<DetailsOverlayState>(null)
    const closeDetailsOverlay = React.useCallback(() => setDetailsOverlay(null), [])
    const currentUserId = currentUser?.id ?? authUserId ?? null
    const openUnitReviewModal = React.useCallback((row: BsoUnitAnalyticsRow) => {
        setUnitReviewModal({
            row,
            temporaryReasons: row.unitReview?.temporaryReasons ?? [],
            permanentReasons: row.unitReview?.permanentReasons ?? [],
            saving: false,
            error: null
        })
    }, [])
    const closeUnitReviewModal = React.useCallback(() => {
        setUnitReviewModal((prev) => prev?.saving === true ? prev : null)
    }, [])
    const openManualViewingModal = React.useCallback((row: BsoUnitAnalyticsRow) => {
        setManualViewingUnit(row)
    }, [])
    const closeManualViewingModal = React.useCallback(() => {
        setManualViewingUnit(null)
    }, [])
    const openManualOfferModal = React.useCallback((row: BsoUnitAnalyticsRow) => {
        setManualOfferUnit(row)
    }, [])
    const closeManualOfferModal = React.useCallback(() => {
        setManualOfferUnit(null)
    }, [])
    const openManualAdvertisementModal = React.useCallback((row: BsoUnitAnalyticsRow) => {
        setManualAdvertisementUnit(row)
    }, [])
    const closeManualAdvertisementModal = React.useCallback(() => {
        setManualAdvertisementUnit(null)
    }, [])
    const updateReviewReasons = React.useCallback((category: UnitReviewCategory, reasons: string[]) => {
        setUnitReviewModal((prev) => {
            if (prev == null) return prev
            const currentReasons = category === 'temporary' ? prev.temporaryReasons : prev.permanentReasons
            const nextReasons = reasons.map((value) => currentReasons.find((reason) => reason.value === value) ?? {
                value,
                label: getUnitReviewReasonLabel(category, value)
            })
            return category === 'temporary'
                ? { ...prev, temporaryReasons: nextReasons, error: null }
                : { ...prev, permanentReasons: nextReasons, error: null }
        })
    }, [])
    const saveUnitReview = React.useCallback(async () => {
        if (unitReviewModal == null) return
        const primaryCategory: UnitReviewCategory | null = unitReviewModal.temporaryReasons.length > 0 ? 'temporary' : unitReviewModal.permanentReasons.length > 0 ? 'permanent' : null
        const primaryReason = primaryCategory === 'temporary' ? unitReviewModal.temporaryReasons[0] : primaryCategory === 'permanent' ? unitReviewModal.permanentReasons[0] : null

        const unitReview = {
            temporaryReasons: unitReviewModal.temporaryReasons,
            permanentReasons: unitReviewModal.permanentReasons,
            updatedAt: new Date().toISOString(),
            updatedBy: currentUserId,
            ...(primaryCategory != null && primaryReason != null ? {
                category: primaryCategory,
                reason: primaryReason.value,
                label: primaryReason.label
            } : {})
        }

        setUnitReviewModal((prev) => prev == null ? prev : { ...prev, saving: true, error: null })
        const response = await dispatch(updateUnitReview(unitReviewModal.row.unitId, { review: unitReview }))
        if (response?.isSuccessful === true) {
            setUnitReviewModal(null)
            return
        }
        setUnitReviewModal((prev) => prev == null ? prev : { ...prev, saving: false, error: getUnitReviewError(response) })
    }, [currentUserId, dispatch, unitReviewModal])
    const openUnitDetails = React.useCallback((title: string, subtitle: string, items: BsoStatisticsUnitDetailItem[]) => {
        if (items.length > 0) setDetailsOverlay({ title, subtitle, items })
    }, [])
    const openSoldPeriodDetails = React.useCallback((period: StatisticPeriod) => {
        const items = period.events.reduce<BsoStatisticsUnitDetailItem[]>((details, event) => {
            const unitDetail = statistics.soldUnitDetails.find((unit) => unit.unitId === event.unitId)
            if (unitDetail != null) {
                details.push({
                    ...unitDetail,
                    offerId: event.offerId,
                    offerRoute: event.offerRoute,
                    processStartDate: event.processStartDate,
                    processEndDate: event.processEndDate,
                    endDate: event.processEndDate,
                    endReason: `Completed offer #${String(event.offerId)}`,
                    isProcessEndDateInferred: event.isProcessEndDateInferred,
                    daysPublished: event.daysPublished
                })
            }
            return details
        }, [])
        openUnitDetails(`Sold / Off-market - ${period.label}`, `${String(items.length)} units. Showing BSO units for this period.`, items)
    }, [openUnitDetails, statistics.soldUnitDetails])

    return (
        <div className="bso-statistics">
            <div className="bso-statistics__container">
                <header className="bso-statistics__header">
                    <div>
                        <h1>BSO Unit Statistics</h1>
                        <p>Overview of active BSO inventory, successful off-market exits, and publication outcomes.</p>
                    </div>
                    <Link className="btn btn-light" to="/landlord/my_units">Back to My Units</Link>
                </header>

                <div className="bso-statistics__kpis">
                    <KpiCard title="Total Units" details={statistics.totalUnitDetails} onOpen={() => openUnitDetails('Total BSO Units', `${String(statistics.totalUnitDetails.length)} units`, statistics.totalUnitDetails)}>
                        <small>Rental: {statistics.totalRentalUnits}</small>
                        <small>Sale: {statistics.totalSaleUnits}</small>
                    </KpiCard>
                    <KpiCard title="Catalog visible" details={statistics.activeUnitDetails} onOpen={() => openUnitDetails('Catalog-visible BSO Units', `${String(statistics.activeUnitDetails.length)} units`, statistics.activeUnitDetails)}>
                        <small>Rental: {statistics.activeRentals}</small>
                        <small>Sale: {statistics.activeSales}</small>
                    </KpiCard>
                    <KpiCard title="Sold / Rented Units" details={statistics.soldUnitDetails} onOpen={() => openUnitDetails('Sold / Rented Units', `${String(statistics.soldUnitDetails.length)} units`, statistics.soldUnitDetails)}>
                        <small>Rent: {statistics.soldRentUnits}</small>
                        <small>Sold: {statistics.soldSaleUnits}</small>
                    </KpiCard>
                </div>

                <section className="bso-statistics__activity-summary">
                    <div className="bso-statistics__activity-summary-head">
                        <div>
                            <h2>Activity Summary</h2>
                            <p className="text-muted mb-0">Aggregated advertisement, viewing, and offer activity for loaded BSO units.</p>
                        </div>
                    </div>
                    <div className="bso-statistics__activity-grid">
                        <ActivitySummaryCard title="Advertisements" summary={activitySummary.advertisements} />
                        <ActivitySummaryCard title="Viewings" summary={activitySummary.viewings} />
                        <ActivitySummaryCard title="Offers" summary={activitySummary.offers} />
                    </div>
                </section>

                <section className="card bso-statistics__section bso-statistics__sold-section">
                    <h2>Sold / Off-market by Completed Offers</h2>
                    <p className="text-muted mb-1">Only units with Sold status and a linked Completed offer counted.</p>
                    <small className="text-muted d-block mb-3">Average days from unit listing date to completed offer process date.</small>
                    <PeriodRows periods={statistics.soldPeriods} onOpenPeriod={openSoldPeriodDetails} />
                    {statistics.soldNeedsDataReview > 0 && (
                        <div className="alert alert-light mt-4 mb-0">
                            Needs data review: {statistics.soldNeedsDataReview} Sold unit offer(s) are Completed but have no ProcessEndDate or ProcessStartDate.
                        </div>
                    )}
                </section>

                <section className="card bso-statistics__section">
                    <div className="bso-statistics__section-header">
                        <div>
                            <h2>Units Analytics</h2>
                        </div>
                        <div className="bso-statistics__analytics-filters" aria-label="Units analytics filters">
                            <div className="bso-statistics__filter-group">
                                <label htmlFor="units-analytics-search">Search units</label>
                                <input
                                    id="units-analytics-search"
                                    className="form-control form-control-sm bso-statistics__analytics-search-input"
                                    type="search"
                                    value={analyticsSearch}
                                    placeholder="Title, building, address, unit or ref"
                                    onChange={(event) => {
                                        setAnalyticsSearch(event.target.value)
                                        setAnalyticsPage(1, true)
                                    }}
                                />
                            </div>
                            <div className="bso-statistics__filter-group">
                                <label htmlFor="units-analytics-sort">Sort by</label>
                                <select id="units-analytics-sort" className="form-select form-select-sm" value={analyticsSort} onChange={(event) => { setAnalyticsSort(event.target.value as AnalyticsSort); setAnalyticsPage(1, true) }}>
                                    <option value="newest">Newest added</option>
                                    <option value="oldest">Oldest added</option>
                                    <option value="price-desc">Price: high to low</option>
                                    <option value="price-asc">Price: low to high</option>
                                </select>
                            </div>
                            <div className="bso-statistics__filter-group">
                                <span>Market status</span>
                                <div className="bso-statistics__filter-toggle" role="group" aria-label="Market status filter">
                                    {MARKET_STATUS_FILTERS.map((filter) => (
                                        <button
                                            className={marketStatusFilter === filter.value ? 'active' : ''}
                                            key={filter.value}
                                            type="button"
                                            onClick={() => {
                                                setMarketStatusFilter(filter.value)
                                                setAnalyticsPage(1, true)
                                            }}
                                        >
                                            {filter.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="bso-statistics__filter-group">
                                <span>Listing type</span>
                                <div className="bso-statistics__filter-toggle" role="group" aria-label="Listing type filter">
                                    {LISTING_TYPE_FILTERS.map((filter) => (
                                        <button
                                            className={listingTypeFilter === filter.value ? 'active' : ''}
                                            key={filter.value}
                                            type="button"
                                            onClick={() => {
                                                setListingTypeFilter(filter.value)
                                                setAnalyticsPage(1, true)
                                            }}
                                        >
                                            {filter.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    {filteredOccupiedAlertCount > 0 && (
                        <div className="bso-statistics__occupied-summary" title="Update unit status">
                            {filteredOccupiedAlertCount} occupied unit{filteredOccupiedAlertCount === 1 ? '' : 's'} need status update
                        </div>
                    )}
                    <UnitAnalyticsTable
                        rows={paginatedAnalyticsRows}
                        totalRows={filteredAnalyticsRows.length}
                        currentPage={analyticsPage}
                        totalPages={analyticsTotalPages}
                        occupiedAlertByUnitId={occupiedAlertByUnitId}
                        advertises={advertises}
                        users={users}
                        isLoading={analyticsLoading}
                        error={analyticsError}
                        onOpenUnitReview={openUnitReviewModal}
                        onOpenManualViewing={openManualViewingModal}
                        onOpenManualOffer={openManualOfferModal}
                        onOpenManualAdvertisement={openManualAdvertisementModal}
                        onPageChange={setAnalyticsPage}
                    />
                </section>

                {/* <section className="card bso-statistics__section">
                    <h2>Removed From Publication</h2>
                    <div className="alert alert-light mb-0">
                        Removed from publication statistics require a reliable unpublished date. No safe source was found yet.
                    </div>
                </section> */}

                {/* <section className="card bso-statistics__section">
                    <h2>Active Offer Pipeline</h2>
                    <p className="text-muted">In-process offers linked to active BSO units. These are separate from sold/off-market totals.</p>
                    {statistics.activeOfferPipeline.length > 0
                        ? (
                            <div className="bso-statistics__pipeline">
                                {statistics.activeOfferPipeline.map((item) => (
                                    <div className="bso-statistics__pipeline-card" key={item.offerId}>
                                        <div className="bso-statistics__pipeline-main">
                                            <img
                                                className="bso-statistics__pipeline-image"
                                                src={item.unitCoverImage}
                                                alt={item.propertyName}
                                            />
                                            <div className="bso-statistics__pipeline-content">
                                                <div className="bso-statistics__pipeline-title">
                                                    <span> {item.unitReference}</span>
                                                    <span>{item.propertyName}</span>
                                                </div>
                                                <div className="bso-statistics__pipeline-grid">
                                                    <div><span>Status</span><strong>{item.status}</strong></div>
                                                    <div><span>Process start</span><strong>{formatAdminDubaiDate(item.processStartDate)}</strong></div>
                                                    <div><span>Process end</span><strong>{item.processEndDate != null ? formatAdminDubaiDate(item.processEndDate) : 'No end date'}</strong></div>
                                                </div>
                                                <div className="bso-statistics__pipeline-actions">
                                                    {item.offerRoute != null
                                                        ? <Link className="btn btn-sm btn-bso" to={item.offerRoute}>Open offer details</Link>
                                                        : <span className="text-muted fs-8">Offer details unavailable</span>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bso-statistics__agent">
                                            {item.agentName != null
                                                ? (
                                                    <>
                                                        <strong>{item.agentName}</strong>
                                                        {item.agentCompany && <small>Company: {item.agentCompany}  </small>}
                                                        <small>Birth year: {item.agentBirthYear ?? 'Not set'}</small>
                                                        {item.agentRoute != null && <Link to={item.agentRoute}>Open agent</Link>}
                                                    </>
                                                )
                                                : <small>Agent details unavailable</small>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                        : <div className="alert alert-light mb-0">No active BSO offer pipeline items.</div>}
                </section> */}
            </div>
            <BsoStatisticsDetailsOverlay state={detailsOverlay} onClose={closeDetailsOverlay} analyticsRowsByUnitId={analyticsRowsByUnitId} />
            <ManualAdvertisementModal unit={manualAdvertisementUnit} users={users} onClose={closeManualAdvertisementModal} />
            <ManualOfferModal unit={manualOfferUnit} users={users} onClose={closeManualOfferModal} />
            <ManualViewingModal unit={manualViewingUnit} users={users} onClose={closeManualViewingModal} />
            <UnitReviewModal
                state={unitReviewModal}
                onClose={closeUnitReviewModal}
                onReasonsChange={updateReviewReasons}
                onSave={() => { void saveUnitReview() }}
            />
        </div>
    )
}
